import React from 'react';
import { ProductRecommendationsJsonFormattingOptions } from '@narvar/nth-hook-loma';
import { useCustomComponentStateContext } from '../../hooks/useCustomComponentState';
import ProductImage from '../ProductImage';
import ProductText from '../ProductText';
import ProductRating from '../ProductRating';
import { MappedItem } from '../../hooks/useProductRecsApi';

export interface TilePartsProps {
  itemInfo: MappedItem;
}

export const renderGranularPrice = (
  price: string | number,
  options: ProductRecommendationsJsonFormattingOptions,
  locale: string = navigator.language,
): string => {
  const { currency, offset } = options;

  let parsedPrice: number;

  if (typeof price === 'string') {
    parsedPrice = Number.parseInt(price.replace(/\D/g, ''), 10);
  } else {
    parsedPrice = price;
  }

  const formattedPrice =
    parsedPrice &&
    Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(parsedPrice / 10 ** (offset ?? 2));

  return formattedPrice || '';
};

function TileParts({ itemInfo }: TilePartsProps) {
  const { state } = useCustomComponentStateContext();

  const { localeLanguage, localeCountry } = state?.pageContext || {};
  const locale =
    localeLanguage && localeCountry
      ? `${localeLanguage}-${localeCountry}`
      : localeLanguage || navigator.language;

  const retailerTheme = state?.queryData?.retailerTheme;

  const { itemsMapping = [] } =
    ((state?.queryData?.retailerSettings?.productRecommendations?.type ===
      'JSON' ||
      state?.queryData?.retailerSettings?.productRecommendations?.type ===
        'CERTONA') &&
      state?.queryData?.retailerSettings?.productRecommendations) ||
    {};

  const headersSmallNone: unknown = retailerTheme?.headersSmallNone || {};
  const bodytextDefaultNone: unknown = retailerTheme?.bodytextDefaultNone || {};

  const productrecsrecimageNoneNone: unknown =
    retailerTheme?.productrecsrecimageNoneNone || {};
  const productrecsrectextNameNone: unknown =
    retailerTheme?.productrecsrectextNameNone || headersSmallNone || {};
  const productrecsrecpriceNoneNone: unknown =
    retailerTheme?.productrecsrecpriceNoneNone || bodytextDefaultNone || {};
  const productrecsrectextBrandNone: unknown =
    retailerTheme?.productrecsrectextBrandNone || headersSmallNone || {};
  const productrecsrecpriceSaleNone: unknown =
    retailerTheme?.productrecsrecpriceSaleNone || bodytextDefaultNone || {};

  const defaultGranularOptions = {
    currency: 'USD',
    offset: 2,
  };
  const productRecsPriceGranularOptions =
    itemsMapping.find((item) => item.type === 'PRICE_GRANULAR')?.options ||
    defaultGranularOptions;
  const productRecsSalePriceGranularOptions =
    itemsMapping.find((item) => item.type === 'SALE_PRICE_GRANULAR')?.options ||
    defaultGranularOptions;

  return (
    <>
      {(Object.entries(itemInfo) || []).map(([key, value]) => {
        const attrName = key;
        const elementKey = `${attrName}${
          typeof value === 'string' ? value : ''
        }`;

        switch (attrName) {
          case 'IMAGE':
            return (
              <ProductImage
                key={elementKey}
                s={{
                  marginBottom: '0.75rem',
                  ...(productrecsrecimageNoneNone &&
                  typeof productrecsrecimageNoneNone === 'object'
                    ? productrecsrecimageNoneNone
                    : {}),
                }}
                src={typeof value === 'string' ? value : ''}
              />
            );
          case 'RATING':
            return (
              <ProductRating
                key={elementKey}
                numberStars={
                  typeof value === 'number' || typeof value === 'string'
                    ? Number(value)
                    : undefined
                }
              />
            );
          case 'REVIEWS':
            return (
              <ProductText
                key={elementKey}
                s={{
                  margin: '0.25rem 0',
                  ...(productrecsrectextBrandNone &&
                  typeof productrecsrectextBrandNone === 'object'
                    ? productrecsrectextBrandNone
                    : {}),
                }}
                text={
                  (typeof value === 'number' && value > 0) ||
                  (typeof value === 'string' && value.length > 0)
                    ? `${String(value).trim()} Reviews`
                    : undefined
                }
              />
            );
          case 'BRAND':
            return (
              <ProductText
                key={elementKey}
                s={{
                  margin: '0.25rem 0',
                  ...(productrecsrectextBrandNone &&
                  typeof productrecsrectextBrandNone === 'object'
                    ? productrecsrectextBrandNone
                    : {}),
                }}
                text={typeof value === 'string' ? value : ''}
              />
            );
          case 'NAME':
            return (
              <ProductText
                key={elementKey}
                s={{
                  margin: '0.25rem 0',
                  ...(productrecsrectextNameNone &&
                  typeof productrecsrectextNameNone === 'object'
                    ? productrecsrectextNameNone
                    : {}),
                }}
                text={typeof value === 'string' ? value : ''}
              />
            );
          case 'PRICE':
            return (
              <ProductText
                key={elementKey}
                s={{
                  margin: '0.25rem 0',
                  ...(productrecsrecpriceNoneNone &&
                  typeof productrecsrecpriceNoneNone === 'object'
                    ? productrecsrecpriceNoneNone
                    : {}),
                }}
                text={
                  typeof value === 'string' || typeof value === 'number'
                    ? value.toString()
                    : ''
                }
              />
            );
          case 'PRICE_GRANULAR':
            return (
              <ProductText
                key={elementKey}
                s={{
                  margin: '0.25rem 0',
                  ...(productrecsrecpriceNoneNone &&
                  typeof productrecsrecpriceNoneNone === 'object'
                    ? productrecsrecpriceNoneNone
                    : {}),
                }}
                text={renderGranularPrice(
                  typeof value === 'string' || typeof value === 'number'
                    ? value
                    : '',
                  productRecsPriceGranularOptions,
                  locale,
                )}
              />
            );
          case 'SALE_PRICE':
            return (
              <ProductText
                key={elementKey}
                s={{
                  marginTop: '0.25rem',
                  ...(productrecsrecpriceSaleNone &&
                  typeof productrecsrecpriceSaleNone === 'object'
                    ? productrecsrecpriceSaleNone
                    : {}),
                }}
                text={typeof value === 'string' ? value : ''}
              />
            );
          case 'SALE_PRICE_GRANULAR':
            return (
              <ProductText
                key={elementKey}
                s={{
                  marginTop: '0.25rem',
                  ...(productrecsrecpriceSaleNone &&
                  typeof productrecsrecpriceSaleNone === 'object'
                    ? productrecsrecpriceSaleNone
                    : {}),
                }}
                text={renderGranularPrice(
                  typeof value === 'string' || typeof value === 'number'
                    ? value
                    : '',
                  productRecsSalePriceGranularOptions,
                  locale,
                )}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

export default TileParts;
